// TMOS Setup Wizard Service
// Date: December 19, 2025
// Purpose: Frontend service for setup wizard API calls

import api from './api';

export type SetupType = 'TENANT_ADMIN' | 'TENANT_USER' | 'INVITED_USER';

export interface SetupStep {
  id: string;
  setup_type: string;
  step_id: string;
  step_order: number;
  step_name: string;
  step_description?: string;
  step_component?: string;
  is_required: boolean;
  is_conditional: boolean;
  condition_logic?: any;
  estimated_time_minutes?: number;
  help_text?: string;
}

export interface SetupProgress {
  user_id: string;
  tenant_id?: string;
  setup_type: SetupType;
  current_step_id?: string;
  completed_step_ids: string[];
  setup_data: Record<string, any>;
  completion_percentage: number;
  is_complete: boolean;
  skipped_at?: string;
  last_accessed_at?: string;
  started_at: string;
  completed_at?: string;
}

export interface TenantSetupStatus {
  id: string;
  tenant_id: string;
  setup_type: string;
  is_complete: boolean;
  completed_steps: string[];
  setup_data: Record<string, any>;
  completed_at?: string;
  completed_by?: string;
}

export const setupWizardService = {
  // Get setup steps for a setup type
  async getSetupSteps(setupType: SetupType): Promise<SetupStep[]> {
    const response = await api.get(`/setup-wizard/steps/${setupType}`);
    return response.data.steps || [];
  },

  // Get user setup progress
  async getUserSetupProgress(setupType: SetupType): Promise<SetupProgress | null> {
    const response = await api.get(`/setup-wizard/user/${setupType}/progress`);
    return response.data.progress || null;
  },

  // Initialize user setup
  async initializeUserSetup(setupType: SetupType): Promise<SetupProgress> {
    const response = await api.post('/setup-wizard/user/initialize', { setupType });
    return response.data.progress;
  },

  // Complete a setup step
  async completeStep(setupType: SetupType, stepId: string, stepData?: Record<string, any>): Promise<SetupProgress> {
    const response = await api.post(`/setup-wizard/user/${setupType}/step/${stepId}/complete`, stepData || {});
    return response.data.progress;
  },

  // Complete user setup
  async completeUserSetup(setupType: SetupType): Promise<SetupProgress> {
    const response = await api.post(`/setup-wizard/user/${setupType}/complete`);
    return response.data.progress;
  },

  // Skip user setup
  async skipUserSetup(setupType: SetupType): Promise<SetupProgress> {
    const response = await api.post(`/setup-wizard/user/${setupType}/skip`);
    return response.data.progress;
  },

  // Get tenant setup status
  async getTenantSetupStatus(): Promise<TenantSetupStatus | null> {
    const response = await api.get('/setup-wizard/tenant/status');
    return response.data.status || null;
  },

  // Initialize tenant setup
  async initializeTenantSetup(): Promise<TenantSetupStatus> {
    const response = await api.post('/setup-wizard/tenant/initialize');
    return response.data.status;
  },

  // Complete tenant setup step
  async completeTenantStep(stepId: string, stepData?: Record<string, any>): Promise<TenantSetupStatus> {
    const response = await api.post(`/setup-wizard/tenant/step/${stepId}/complete`, stepData || {});
    return response.data.status;
  },

  // Complete tenant setup
  async completeTenantSetup(): Promise<TenantSetupStatus> {
    const response = await api.post('/setup-wizard/tenant/complete');
    return response.data.status;
  },

  // Check if tenant setup is complete
  async isTenantSetupComplete(): Promise<boolean> {
    const response = await api.get('/setup-wizard/tenant/check-complete');
    return response.data.is_complete || false;
  }
};

